import { useCallback, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'
import { useMediaQuery } from '@react-hookz/web'
import type { CellContext, ColumnDef } from '@tanstack/react-table'
import { clsx } from 'clsx'
import { observer } from 'mobx-react'

import { GLOBAL_ROUTE_PATHS } from 'Router/Router.constants'

import { Table } from 'Components/_theme'
import { FormatDate } from 'Components/FormatDate/FormatDate'
import { ImageWithFallback } from 'Components/ImageWithFallback/ImageWithFallback'
import { RentalStatusBadge } from 'Components/RentalStatusBadge/RentalStatusBadge'
import { RentalStatus } from 'Components/RentalStatusBadge/RentalStatusBadge.types'
import { PassiveTagCell } from 'Components/Tagging/PassiveTagCell'
import { mapGraphqlTags } from 'Components/Tagging/tagging.utils'
import { useShowGraphqlListTags } from 'Components/Tagging/useShowGraphqlListTags'

import { useIsOrganisation } from 'Hooks'

import { buttonize, getFullContactName } from 'Utilities'

import { PORTAL_BREAKPOINTS } from 'Constants/constants'
import { E2E_TABLE_LINK_RENTALS } from 'Constants/e2e'

import type { DeviceTypeEnum } from 'Types/deviceTypes'

import { composePath } from 'Portal/src/utilities/composePath/composePath.util'

import type {
  ChildEmployeeRentalsWithUrl,
  ChildOrganisationRentalsWithUrl,
  EmployeeRentalsWithUrl,
  OrganisationRentalsWithUrl,
  RentalsTableProps,
} from './RentalsTable.types'

type DataType =
  | ChildEmployeeRentalsWithUrl
  | ChildOrganisationRentalsWithUrl
  | EmployeeRentalsWithUrl
  | OrganisationRentalsWithUrl

export const RentalsTable = observer((props: RentalsTableProps) => {
  const {
    rentals,
    isActiveEmployee,
    isLoading,
    isSearching,
    onOpenPreviewColumn,
    previewedRowIndex,
  } = props

  const isOrganisation = useIsOrganisation()
  const intl = useIntl()
  const showTags = useShowGraphqlListTags()
  const { employeeId, organisationId } = useParams<{
    employeeId: string
    organisationId: string
  }>()

  const navigate = useNavigate()
  const isTablet = useMediaQuery(`(min-width: ${PORTAL_BREAKPOINTS.LG})`)

  const generateUrl = useCallback(
    (rentalId: string) =>
      composePath({
        isOrganisation,
        params: {
          employeeId,
          organisationId,
          rentalId,
        },
        paths: {
          employee: GLOBAL_ROUTE_PATHS.employee.rental,
          organisation: GLOBAL_ROUTE_PATHS.organisation.rental,
        },
      }),

    [employeeId, isOrganisation, organisationId],
  )

  const handleEnterDetails = useCallback(
    (
      _event:
        | React.MouseEvent<HTMLDivElement>
        | React.KeyboardEvent<HTMLDivElement>,
      id: string,
      url: string,
      rowIndex: string,
    ) => {
      if (!isTablet) {
        navigate(url)

        return
      }

      if (typeof onOpenPreviewColumn === 'function') {
        onOpenPreviewColumn(id, rowIndex)
      }
    },
    [isTablet, navigate, onOpenPreviewColumn],
  )

  const renderNameHeader = useCallback(
    () => <FormattedMessage key="name" id="DeviceListContent_header__name" />,
    [],
  )

  const renderEmployeeHeader = useCallback(
    () => (
      <FormattedMessage key="name" id="DeviceListContent_header__employee" />
    ),
    [],
  )

  const renderStartDateHeader = useCallback(
    () => (
      <FormattedMessage key="name" id="DeviceListContent_header__start_date" />
    ),
    [],
  )

  const renderNameCell = useCallback(
    ({ cell }: CellContext<DataType, unknown>) => {
      const rental = cell.row.original

      return (
        <div
          className="d-inline-flex"
          {...buttonize(handleEnterDetails, rental.id, rental.url, cell.row.id)}
        >
          <div className="d-flex justify-content-center align-items-center me-4 Table-device-image">
            <ImageWithFallback
              alt={rental.deviceDefinition?.name}
              placeholderType={
                rental.deviceDefinition?.productType
                  ? (rental.deviceDefinition.productType as DeviceTypeEnum)
                  : undefined
              }
              src={rental.deviceDefinition?.imageURL || ''}
            />
          </div>

          <div className="d-flex flex-column justify-content-center text-ellipsis">
            {(rental.actions.showStandardUpgradeAction ||
              rental.actions.showDeviceProlongingAction) && (
              <div>
                <RentalStatusBadge status={RentalStatus.new_device} />
              </div>
            )}

            {rental.actions.showEarlyUpgradeAction && (
              <div className="mb-1">
                <RentalStatusBadge status={RentalStatus.upgrade} />
              </div>
            )}

            <div className="d-flex align-items-center">
              <strong className="text-ellipsis">
                <span>{rental.deviceDefinition?.name}</span>
              </strong>
            </div>

            <div className="d-flex table-text-subtitle text-muted">
              {(rental.stocklistItem?.imei ||
                rental.stocklistItem?.serialNumber) && (
                <div className="d-flex table-text-subtitle text-muted">
                  {rental.stocklistItem?.imei ? (
                    <>
                      <span>IMEI: </span>

                      <span className="text-ellipsis">
                        {rental.stocklistItem.imei}
                      </span>
                    </>
                  ) : (
                    <>
                      <span>SN: </span>

                      <span className="text-ellipsis">
                        {rental.stocklistItem.serialNumber}
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )
    },
    [handleEnterDetails],
  )

  const renderEmployeeCell = useCallback(
    ({ cell }: CellContext<DataType, unknown>) => {
      const isChildElement = cell.row.depth > 0

      if (isChildElement) {
        return null
      }

      const rental = cell.row.original

      return (
        <div
          className="d-flex align-items-center h-100"
          {...buttonize(
            handleEnterDetails,
            rental.id,
            rental.url,
            cell.row.index,
          )}
        >
          <span className="text-ellipsis">
            {rental.employee?.contact?.firstName
              ? getFullContactName(rental.employee.contact, { intl })
              : '-'}
          </span>
        </div>
      )
    },
    [handleEnterDetails, intl],
  )

  const renderStartDateCell = useCallback(
    ({ cell }: CellContext<DataType, unknown>) => {
      const isChildElement = cell.row.depth > 0

      if (isChildElement) {
        return null
      }

      const rental = cell.row.original

      return (
        <div
          className="d-flex align-items-center h-100"
          {...buttonize(
            handleEnterDetails,
            rental.id,
            rental.url,
            cell.row.index,
          )}
        >
          <FormatDate value={new Date(rental.startAt)} />
        </div>
      )
    },
    [handleEnterDetails],
  )

  const renderTagsHeader = useCallback(
    () => <FormattedMessage key="tags" id="Tags" />,
    [],
  )

  const renderTagsCell = useCallback(
    ({ cell }: CellContext<DataType, unknown>) => {
      const rental = cell.row.original

      // Parents and children carry their own tags; both come straight from the API.
      return (
        <PassiveTagCell
          tags={mapGraphqlTags('tags' in rental ? rental.tags : null)}
        />
      )
    },
    [],
  )

  const columns = useMemo<ColumnDef<DataType>[]>(() => {
    const tableColumns: ColumnDef<DataType>[] = [
      {
        cell: renderNameCell,
        header: renderNameHeader,
        id: 'name',
      },
      {
        cell: renderEmployeeCell,
        header: renderEmployeeHeader,
        id: 'employee',
      },
      {
        cell: renderStartDateCell,
        header: renderStartDateHeader,
        id: 'start_date',
      },
    ]

    if (showTags) {
      tableColumns.push({
        cell: renderTagsCell,
        enableSorting: false,
        header: renderTagsHeader,
        id: 'tags',
      })
    }

    return tableColumns.filter(column => {
      // don't show employee when logged in as employee - they are the same
      if (isActiveEmployee && column.id === 'employee') {
        return false
      }

      return true
    })
  }, [
    isActiveEmployee,
    renderEmployeeCell,
    renderEmployeeHeader,
    renderNameCell,
    renderNameHeader,
    renderStartDateCell,
    renderStartDateHeader,
    renderTagsCell,
    renderTagsHeader,
    showTags,
  ])

  const data = useMemo(
    () =>
      rentals.map(rental => {
        const childRentals = rental.children?.length
          ? rental.children.map(child => ({
              ...child,
              url: child?.id ? generateUrl(child.id) : '',
            }))
          : []

        return {
          ...rental,
          children: childRentals,
          url: generateUrl(rental.id),
        }
      }),
    [generateUrl, rentals],
  )

  return (
    <Table
      // Remounting on a gate flip stops react-table from reusing the row cells
      // of the Tags column once it is gone.
      key={showTags ? 'withTags' : 'withoutTags'}
      className={clsx(
        'Table--rentals',
        isOrganisation ? 'Table--isAdmin' : 'Table--isEmployee',
      )}
      columns={columns}
      data={data}
      e2eRowId={E2E_TABLE_LINK_RENTALS}
      isLoading={isLoading}
      isSearching={isSearching}
      previewedRowIndex={previewedRowIndex}
      withSubrows
    />
  )
})
